/**
 * Live Trading Positions Table Row & Header Model Builders.
 *
 * Generates cell definitions for reconcileTbody with DOM-owned live LTP / UR.P&L cells,
 * broker confirmation badges, and position exit actions.
 */

export interface LivePositionCell {
  html?: string;
  text?: string | number;
  live?: boolean;
  id?: string;
  cls?: string;
}

export interface LivePositionRowModel {
  key: string;
  cls: string;
  cells: LivePositionCell[];
}

export interface LivePositionRowOptions {
  showQty: boolean;
  exits: Record<string, any>;
  liveLp: (p: any) => number | null;
  avgCell: (key: string, side: "buy" | "sell") => string;
  qtyCell: (key: string, side: "buy" | "sell") => string;
  cid: (type: string, key: string) => string;
}

/**
 * Builds positions table header HTML with optional broker Buy/Sell Qty columns.
 */
export function buildPositionsHeadHtml(showQty: boolean): string {
  return (
    "<th>Symbol</th><th>Net Qty</th><th>Buy Avg</th>" +
    (showQty ? "<th>Buy Qty</th>" : "") +
    "<th>Sell Avg</th>" +
    (showQty ? "<th>Sell Qty</th>" : "") +
    "<th>Avg</th><th>LTP</th><th>R.P&L</th><th>UR.P&L</th><th>Prd</th><th></th>"
  );
}

/**
 * Builds action buttons HTML for an open position row (50%, 75%, Close, SL·TGT).
 */
export function buildPositionActionButtons(
  p: any,
  netQty: number,
  hasExit: boolean,
): string {
  const tsym = p.tsym || "";
  const exch = p.exch || "NFO";
  const prd = p.prd || "";
  const avg = p.avg || 0;
  const exitLabel = hasExit ? "⛨ SL·TGT ✓" : "SL·TGT";

  return `<span class="manage-row">
             <button class="mbtn qty" onclick="closePosition('${tsym}','${exch}',${netQty},'${prd}',null,0.5)">50%</button>
             <button class="mbtn qty" onclick="closePosition('${tsym}','${exch}',${netQty},'${prd}',null,0.75)">75%</button>
             <button class="mbtn close" onclick="closePosition('${tsym}','${exch}',${netQty},'${prd}',null,1)">Close</button>
             <button class="mbtn sltgt" onclick="openExitModal('${tsym}','${exch}',${netQty},'${prd}',${avg})">${exitLabel}</button>
           </span>`;
}

/**
 * Constructs reconciled row models from open and closed live positions.
 */
export function buildLivePositionRowModels(
  positionsList: any[],
  options: LivePositionRowOptions,
): LivePositionRowModel[] {
  if (!Array.isArray(positionsList)) return [];

  const open = positionsList.filter((p) => p && p.netqty !== 0);
  const closed = positionsList.filter((p) => p && p.netqty === 0 && p.realized);
  const showQty = !!options.showQty;
  const rows: LivePositionRowModel[] = [];

  for (const p of open) {
    const net = p.netqty;
    const pending = p.confirmed
      ? ""
      : '<span class="pending-badge" title="awaiting broker confirmation">⋯</span>';
    const actions = buildPositionActionButtons(p, net, !!options.exits[p.key]);

    rows.push({
      key: p.key,
      cls: "",
      cells: [
        {
          html: `<span class="sym-name">${p.tsym}</span><span class="pos-chip open">OPEN</span>${pending}`,
        },
        { text: net },
        { text: options.avgCell(p.key, "buy") },
        ...(showQty ? [{ text: options.qtyCell(p.key, "buy") }] : []),
        { text: options.avgCell(p.key, "sell") },
        ...(showQty ? [{ text: options.qtyCell(p.key, "sell") }] : []),
        { text: p.avg ? p.avg.toFixed(2) : "-" },
        { live: true, id: options.cid("ltp", p.key) },
        {
          text: Math.abs(p.realized || 0).toFixed(2),
          cls: (p.realized || 0) >= 0 ? "pos" : "neg",
        },
        { live: true, id: options.cid("ur", p.key) },
        { text: p.prd || "" },
        { html: actions },
      ],
    });
  }

  for (const p of closed) {
    const rp = p.realized || 0;
    const lp = options.liveLp(p);

    rows.push({
      key: p.key,
      cls: "pos-closed",
      cells: [
        {
          html: `<span class="sym-name">${p.tsym}</span><span class="pos-chip closed">CLOSED</span>`,
        },
        { text: "0" },
        { text: options.avgCell(p.key, "buy") },
        ...(showQty ? [{ text: options.qtyCell(p.key, "buy") }] : []),
        { text: options.avgCell(p.key, "sell") },
        ...(showQty ? [{ text: options.qtyCell(p.key, "sell") }] : []),
        { text: "-" },
        { text: lp != null ? lp.toFixed(2) : "-" },
        {
          text: Math.abs(rp).toFixed(2),
          cls: rp >= 0 ? "pos" : "neg",
        },
        { text: "0" },
        { text: p.prd || "" },
        { html: "" },
      ],
    });
  }

  return rows;
}
